import { useEffect, useState } from 'react';
import BillingAddressEditor from './BillingAddressEditor';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth, isTTC, isCustomerAdmin } from '../hooks/useAuth';
import { Plus, Star, Edit2, PowerOff, Power } from 'lucide-react';

const COUNTRIES = [
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'MX', label: 'Mexico' },
];

const EMPTY_FORM = {
  nickname: '', attention: '', address1: '', address2: '',
  city: '', state: '', zip: '', country: 'US', phone: '',
  inside_delivery: 0, lift_gate: 0,
};

export function AddressForm({ initial = EMPTY_FORM, onSave, onCancel, title = 'Add Ship-To Address' }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.nickname) return toast.error('Nickname / location name is required');
    if (!form.address1) return toast.error('Address line 1 is required');
    if (!form.city || !form.state || !form.zip) return toast.error('City, state and ZIP are required');
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)', marginBottom: 14 }}>{title}</div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Location Nickname / Number <span className="required">*</span></label>
          <input className="form-input" value={form.nickname} onChange={e => set('nickname', e.target.value)}
            placeholder="e.g. Corporate HQ, Airport Lot B, Site #142" />
        </div>
        <div className="form-group">
          <label className="form-label">Attention / Contact</label>
          <input className="form-input" value={form.attention} onChange={e => set('attention', e.target.value)}
            placeholder="e.g. Receiving Dept, John Smith" />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Address Line 1 <span className="required">*</span></label>
        <input className="form-input" value={form.address1} onChange={e => set('address1', e.target.value)}
          placeholder="Street address, P.O. Box" />
      </div>
      <div className="form-group">
        <label className="form-label">Address Line 2</label>
        <input className="form-input" value={form.address2} onChange={e => set('address2', e.target.value)}
          placeholder="Suite, unit, building (optional)" />
      </div>

      <div className="form-row-3">
        <div className="form-group">
          <label className="form-label">City <span className="required">*</span></label>
          <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">State / Province <span className="required">*</span></label>
          <input className="form-input" value={form.state} onChange={e => set('state', e.target.value)}
            maxLength={3} placeholder="IL" />
        </div>
        <div className="form-group">
          <label className="form-label">ZIP / Postal Code <span className="required">*</span></label>
          <input className="form-input" value={form.zip} onChange={e => set('zip', e.target.value)} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Country</label>
          <select className="form-select" value={form.country} onChange={e => set('country', e.target.value)}>
            {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Phone</label>
          <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)}
            placeholder="+1 (555) 000-0000" />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Delivery options</label>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', paddingTop: 4 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.inside_delivery}
              onChange={e => set('inside_delivery', e.target.checked ? 1 : 0)} />
            Inside Delivery
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.lift_gate}
              onChange={e => set('lift_gate', e.target.checked ? 1 : 0)} />
            Lift Gate Service
          </label>
        </div>
        <p className="form-hint">These become the default for orders shipped here (can be overridden per order).</p>
      </div>

      <div className="modal-footer" style={{ paddingTop: 12, marginTop: 4 }}>
        {onCancel && <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>}
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Address'}
        </button>
      </div>
    </div>
  );
}

// Compact dropdown selector used in cart checkout
export function AddressSelector({ customerId, value, onChange, allowAdd = true }) {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    api.get(`/api/addresses/customers/${customerId}/addresses`)
      .then(r => setAddresses(r.data.addresses))
      .catch(() => {});
  };

  useEffect(() => { if (customerId) load(); }, [customerId]);

  const handleAdd = async (form) => {
    const r = await api.post(`/api/addresses/customers/${customerId}/addresses`, form);
    toast.success('Address saved');
    load();
    onChange(r.data.address.id);
    setShowForm(false);
  };

  const handleSetDefault = async (addressId) => {
    await api.patch(`/api/addresses/users/${user.id}/default-address`, { address_id: addressId });
    toast.success('Default address updated');
  };

  if (showForm) {
    return (
      <div className="card" style={{ marginBottom: 0 }}>
        <AddressForm
          title="Add New Ship-To Address"
          onSave={handleAdd}
          onCancel={() => setShowForm(false)}
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
          <label className="form-label">Ship-To Address</label>
          <select
            className="form-select"
            value={value || ''}
            onChange={e => onChange(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">— Select a ship-to address —</option>
            {addresses.map(a => (
              <option key={a.id} value={a.id}>
                {a.nickname}{a.city ? ` — ${a.city}, ${a.state}` : ''}
                {a.id === user?.default_address_id ? ' ★' : ''}
              </option>
            ))}
          </select>
        </div>
        {allowAdd && (
          <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(true)}
            style={{ flexShrink: 0, marginBottom: 1 }}>
            <Plus size={13} /> Add Ship-To
          </button>
        )}
      </div>
      {value && (
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
          {(() => {
            const addr = addresses.find(a => a.id === value);
            if (!addr) return null;
            return (
              <>
                <div style={{ fontSize: 11.5, color: 'var(--gray-600)', lineHeight: 1.5 }}>
                  {addr.attention && <span>{addr.attention} · </span>}
                  {addr.address1}{addr.address2 ? `, ${addr.address2}` : ''}
                  {', '}{addr.city}, {addr.state} {addr.zip}
                  {addr.country !== 'US' ? ` · ${addr.country}` : ''}
                  {addr.phone ? ` · ${addr.phone}` : ''}
                </div>
                {value !== user?.default_address_id && (
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, flexShrink: 0 }}
                    onClick={() => handleSetDefault(value)}>
                    <Star size={11} /> Set as default
                  </button>
                )}
                {value === user?.default_address_id && (
                  <span style={{ fontSize: 11, color: 'var(--gold)', display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Star size={11} fill="currentColor" /> Default
                  </span>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// Full address book management panel (used in Customers admin and customer profile)
export function AddressBookManager({ customerId }) {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // address object or 'new'
  const [billingMode, setBillingMode] = useState('customer');
  const canManage = isTTC(user) || (isCustomerAdmin(user) && user.customer_id === customerId);

  useEffect(() => {
    api.get(`/api/customers/${customerId}`).then(r => setBillingMode(r.data.customer?.billing_mode || r.data.billing_mode || 'customer')).catch(() => {});
  }, [customerId]);

  const load = () => {
    api.get(`/api/addresses/customers/${customerId}/addresses`)
      .then(r => { setAddresses(r.data.addresses); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [customerId]);

  const handleSave = async (form) => {
    if (editing === 'new') {
      await api.post(`/api/addresses/customers/${customerId}/addresses`, form);
      toast.success('Address added');
    } else {
      await api.patch(`/api/addresses/customers/${customerId}/addresses/${editing.id}`, form);
      toast.success('Address updated');
    }
    setEditing(null);
    load();
  };

  const toggleActive = async (addr) => {
    await api.patch(`/api/addresses/customers/${customerId}/addresses/${addr.id}/active`,
      { active: !addr.active });
    toast.success(addr.active ? 'Address deactivated' : 'Address reactivated');
    load();
  };

  const handleDelete = async (addr) => {
    if (!window.confirm(`Permanently delete "${addr.nickname}"? This can't be undone. Past orders keep their shipping details.`)) return;
    try {
      await api.delete(`/api/addresses/customers/${customerId}/addresses/${addr.id}`);
      toast.success('Address deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  };

  const handleSetDefault = async (addressId) => {
    await api.patch(`/api/addresses/users/${user.id}/default-address`, { address_id: addressId });
    toast.success('Default address updated');
  };

  if (editing) {
    return (
      <div className="card">
        <AddressForm
          title={editing === 'new' ? 'Add Ship-To Address' : `Edit — ${editing.nickname}`}
          initial={editing === 'new' ? EMPTY_FORM : editing}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
        />
        {editing !== 'new' && editing.id && billingMode === 'ship_to' && (
          <BillingAddressEditor customerId={customerId} scope="ship_to" refId={editing.id} label="Billing address for this ship-to" />
        )}
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3>Ship-To Addresses</h3>
        <button className="btn btn-gold btn-sm" onClick={() => setEditing('new')}>
          <Plus size={13} /> Add Address
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : addresses.length === 0 ? (
        <div className="empty-state" style={{ padding: 24 }}>No addresses yet.</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nickname / Location</th>
                <th>Address</th>
                <th>City, State</th>
                <th>Country</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Default</th>
                {canManage && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {addresses.map(addr => (
                <tr key={addr.id} style={{ opacity: addr.active ? 1 : 0.55 }}>
                  <td>
                    <strong>{addr.nickname}</strong>
                    {addr.attention && <div className="text-sm text-muted">{addr.attention}</div>}
                  </td>
                  <td className="text-sm">
                    {addr.address1}
                    {addr.address2 && <div className="text-muted">{addr.address2}</div>}
                  </td>
                  <td className="text-sm">{addr.city}, {addr.state} {addr.zip}</td>
                  <td className="text-sm">{COUNTRIES.find(c => c.code === addr.country)?.label || addr.country}</td>
                  <td className="text-sm text-muted">{addr.phone || '—'}</td>
                  <td>
                    <span className={`badge ${addr.active ? 'badge-active' : 'badge-inactive'}`}>
                      {addr.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {addr.id === user.default_address_id ? (
                      <span style={{ color: 'var(--gold)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Star size={12} fill="currentColor" /> Default
                      </span>
                    ) : addr.active ? (
                      <button className="btn btn-ghost btn-sm" onClick={() => handleSetDefault(addr.id)}>
                        <Star size={12} /> Set default
                      </button>
                    ) : null}
                  </td>
                  {canManage && (
                    <td>
                      <div className="actions-cell">
                        {addr.active && (
                          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(addr)}
                            title="Edit">
                            <Edit2 size={13} />
                          </button>
                        )}
                        <button
                          className={`btn btn-sm ${addr.active ? 'btn-ghost' : 'btn-secondary'}`}
                          onClick={() => toggleActive(addr)}
                          title={addr.active ? 'Deactivate' : 'Reactivate'}
                        >
                          {addr.active
                            ? <><PowerOff size={13} /> Deactivate</>
                            : <><Power size={13} /> Reactivate</>}
                        </button>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--brand-red)' }}
                          onClick={() => handleDelete(addr)} title="Delete permanently">
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
